import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getDay, getTimeRange, useDeviceType } from '@vfit/shared/hooks';
import { purify } from '@vfit/shared/themes';
import { slotsTechAppointmentSchema } from './slotsTechAppointment.schema';
import { IUseSlotsTechAppointment } from './slotsTechAppointment.models';

export const useSlotsTechAppointment = ({
  calendarModalButtonLabel,
  confirm,
  dateTimes = [],
  loading,
  noOtherSlotsDescription,
  showOtherAvailabilityLinkLabel,
  stickyOfferButtonLabel,
  track,
}: IUseSlotsTechAppointment) => {
  const [isOpen, setIsOpen] = useState(false);
  const [slotsGroupsMaxHeight, setSlotsGroupsMaxHeight] = useState<number | undefined>();

  const { isTablet, isDesktop } = useDeviceType();

  const [pNoOtherSlotsDescription] = purify([noOtherSlotsDescription]);

  const {
    formState: { isValid: isValidCalendarForm },
    getValues: getCalendarFormValues,
    register: registerCalendarForm,
    reset: resetCalendarForm,
  } = useForm({
    mode: 'all',
    defaultValues: {
      calendarAppointments: '',
    },
  });

  const {
    formState: { isValid: isValidFilteredForm },
    getValues: getFilteredFormValues,
    watch: watchFilteredForm,
    register: registerFilteredForm,
    reset: resetFilteredForm,
  } = useForm({
    mode: 'all',
    defaultValues: {
      filteredAppointments: '',
    },
  });

  useEffect(() => {
    if (loading && isOpen) track?.('Popup disponibilità:ricalcolo', 'LOADER_MODAL');
  }, [loading, isOpen]);

  useEffect(() => {
    if (loading) {
      resetFilteredForm();
      resetCalendarForm();
    }
  }, [loading, resetCalendarForm, resetFilteredForm]);

  const filteredAppointmentsId = watchFilteredForm('filteredAppointments');

  const orderedDateTimes = useMemo(
    () =>
      dateTimes
        .map(({ validFor: { startDateTime, endDateTime }, ...rest }) => ({
          validFor: {
            startDateTime: new Date(Number(startDateTime))?.toISOString(),
            endDateTime: new Date(Number(endDateTime))?.toISOString(),
          },
          ...rest,
        }))
        .sort((a, b) => {
          const aDay = a?.validFor?.startDateTime?.split('T')[0];
          const bDay = b?.validFor?.startDateTime?.split('T')[0];

          // eslint-disable-next-line no-nested-ternary
          return aDay < bDay ? -1 : aDay > bDay ? 1 : 0;
        }),
    [dateTimes]
  );

  const filteredDateTimes = useMemo(() => orderedDateTimes.slice(0, 3), [orderedDateTimes]);

  const selectedFilteredAppointment = useMemo(() => {
    const slot = orderedDateTimes.find(({ id }) => id === filteredAppointmentsId);
    let res = {
      day: '',
      time: '',
    };

    if (slot?.validFor?.startDateTime && slot?.validFor?.endDateTime) {
      res = {
        day: getDay(slot?.validFor?.startDateTime),
        time: getTimeRange(slot?.validFor?.startDateTime, slot?.validFor?.endDateTime),
      };
    }
    return res;
  }, [filteredAppointmentsId, orderedDateTimes]);

  const handleConfirm = useCallback(
    (selectedEventId: string) => {
      const selectedTimeSlot = orderedDateTimes.find(({ id }) => id === selectedEventId);

      if (selectedTimeSlot) {
        confirm?.(selectedTimeSlot);
      }
    },
    [confirm, orderedDateTimes]
  );

  const confirmFromCalendarForm = useCallback(() => {
    track?.(calendarModalButtonLabel || '', 'CONFIRM_MODAL');
    const selectedEventId = getCalendarFormValues('calendarAppointments');

    handleConfirm?.(selectedEventId);
  }, [calendarModalButtonLabel, getCalendarFormValues, handleConfirm, track]);

  const confirmFromFilteredForm = useCallback(() => {
    track?.(stickyOfferButtonLabel, 'CONFIRM_STICKY');
    const selectedEventId = getFilteredFormValues('filteredAppointments');

    handleConfirm?.(selectedEventId);
  }, [getFilteredFormValues, handleConfirm, stickyOfferButtonLabel, track]);

  const open = useCallback(() => {
    track?.(showOtherAvailabilityLinkLabel, 'VIEW_MODAL');
    setIsOpen(true);
  }, [showOtherAvailabilityLinkLabel, track]);

  const close = useCallback(() => {
    track?.('Close Calendar Modal: X', 'CLOSE_MODAL');
    setIsOpen(false);
    resetCalendarForm();
  }, [resetCalendarForm, track]);

  /* Workaround to handle scrolling on calendar
   * Look at: slotsCalendarInput.style.ts
   */
  useEffect(() => {
    if (isOpen) {
      const buttonHeight = 45;
      const footerPaddingTop = 20;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line consistent-return
      setTimeout(() => {
        const modalContent =
          document.getElementById('coverage-tool')?.lastElementChild?.lastElementChild
            ?.lastElementChild?.lastElementChild;
        if (modalContent) {
          if (isTablet || isDesktop) {
            (modalContent as HTMLElement).style.overflow = 'hidden';
            const handleSlotsGroupsMaxHeight = () => {
              requestAnimationFrame(() => {
                setSlotsGroupsMaxHeight(
                  modalContent.clientHeight -
                    buttonHeight -
                    (isTablet ? footerPaddingTop + 88 : footerPaddingTop + 80)
                );
              });
            };
            handleSlotsGroupsMaxHeight();
            window.addEventListener('resize', handleSlotsGroupsMaxHeight);

            return () => {
              window.removeEventListener('resize', handleSlotsGroupsMaxHeight);
            };
          }

          (modalContent as HTMLElement).style.overflow = 'auto';
        }
      }, 300);
    }
  }, [isOpen, isTablet, isDesktop]);

  return {
    close,
    confirmFromCalendarForm,
    confirmFromFilteredForm,
    filteredDateTimes,
    isOpen,
    isValidCalendarForm,
    isValidFilteredForm,
    open,
    orderedDateTimes,
    pNoOtherSlotsDescription,
    slotsGroupsMaxHeight,
    slotsTechAppointmentSchema,
    registerCalendarForm,
    registerFilteredForm,
    selectedFilteredAppointment,
  };
};

